<template>
  <MountingPortal mountTo="#magnifier-portal-container" to="box" append>
    <div class="magnifier">
      <div :class="['cover', { 'dark': !light, 'hide': !finishRendering }]">
        <div ref="content" class="content-wrap" :style="{ 
            transform: `translate(${rect.left}px, ${rect.top}px)`,
            width: `${rect.width}px`, 
            height: `${rect.height}px`,
          }">
          <div 
            id="magnifier-iframe-entry" 
            :class="[
              'content-entry', 
              { 'circle': shape === 'circle' },
            ]"
            :style="{ width: `${rect.width}px`, height: `${rect.height}px` }"
            @mousedown="startDown($event)"
            @touchstart="startDown($event)"
          >
            <div id="magnifier-iframe__entry" class="content__entry"></div>
          </div>
          <ControlDots 
            @movestart="onRectResizeStart" 
            @move="onRectResize"
            @moveend="onRectResizeEnd"
          />
          <ControlToolbar 
            :hide="isResizing || startMoving"
            :rect="rect" 
            :light.sync="light"
            :shape.sync="shape"
            :scale.sync="scale"
            @close="closeMagnifier"
          />
        </div>
      </div>
    </div>
  </MountingPortal>
</template>

<script>
import { DocumentCloner, Bounds } from './DocumentCloner';
import ControlDots from './ControlDots.vue';
import ControlToolbar from './ControlToolbar.vue';

export default {
  name: 'magnifier',
  props: ['type'],
  components: {
    ControlDots,
    ControlToolbar,
  },
  data() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const { width, height, top: centerTop, left: centerLeft } = this.calcInitRect(windowWidth, windowHeight);

    return {
      root: '#app-ct',
      entryNode: '#magnifier-iframe__entry',
      contentNodeClass: 'html2canvas-container',
      screenshotClass: 'electron-screenshot',
      windowWidth,
      windowHeight,
      rect: {
        top: centerTop,
        left: centerLeft,
        width,
        height,
      },
      lastRect: {
        top: centerTop,
        left: centerLeft,
        width,
        height,
      },
      minSize: 40,
      refPoint: {
        top: centerTop,
        left: centerLeft,
      },
      isMove: false,
      startMoving: false,
      isResizing: false,
      disX: 0,
      disY: 0,
      finishRendering: false,
      light: this.type === 'magnifier',
      scale: 1.5,
      shape: 'rect',
    };
  },
  computed: {
    maxSize() {
      return this.calcMaxSize(this.windowWidth, this.windowHeight);
    },
  },
  created() {
    Array.from(document.querySelectorAll('audio')).forEach((audio) => {
      audio.pause();
    });

    const element = document.querySelector(this.root);
    const instanceName = (Math.round(Math.random() * 1000) + Date.now()).toString(16);
    const documentCloner = new DocumentCloner(element, {
      id: instanceName,
      onclone: (doc) => {
        doc.body.style.cssText = 'user-select: none; pointer-events: none;';

        Array.from(doc.querySelectorAll('body > div')).forEach((div) => {
          if (div.id !== 'app-ct') {
            doc.body.removeChild(div);
          }
        });

        const $videos = Array.from(doc.querySelectorAll('video'));

        if ($videos.length > 0) {
          const canvas = document.createElement('canvas');

          $videos.forEach((fakeVideo, i) => {
            const realVideo = $(`${this.root} video`).get(i);
            let screenshot = '';

            if (this.isPc()) {
              canvas.width = realVideo.videoWidth;
              canvas.height = realVideo.videoHeight;
              canvas.getContext('2d').drawImage(realVideo, 0, 0, canvas.width, canvas.height);
              screenshot = canvas.toDataURL('image/png');
            } else {
              screenshot = `${realVideo.src}?vframe/jpg/offset/${Math.ceil(realVideo.currentTime)}`;
            }

            const $img = $('<img>')
              .attr('src', screenshot)
              .css({
                'max-width': '100%',
                'max-height': '100%',
                'width': '100%',
                'height': '100%',
                'object-fit': 'contain',
              });

            fakeVideo.parentNode.replaceChild($img.get(0), fakeVideo);
          });
        }

        Array.from(doc.querySelectorAll('audio')).forEach((audio, i) => {
          audio.currentTime = $(`${this.root} audio`).get(i).currentTime;
        });

        this.finish();
      },
      ignoreElements: (element) => false,
      inlineImages: false,
      copyStyles: false
    });

    const ownerDocument = element.ownerDocument;
    const windowBounds = new Bounds(element.scrollX, element.scrollY, window.innerWidth, window.innerHeight);
    this.$nextTick().then(
      this.$nextTick(() => {
        documentCloner.toIFrame(ownerDocument, windowBounds, this.entryNode);
      })
    );

    this.bindEvents();

    if ($(`${this.root} video`).length > 0) {
      this.$store.commit('sendstopvideoevent', Date.now());
    }
  },
  mounted() {
    document.body.addEventListener('mousemove', this.startMove, false);
    document.body.addEventListener('mouseup', this.startEnd, false);
    document.body.addEventListener('mouseleave', this.startEnd, false);
    document.body.addEventListener('touchmove', this.startMove, false);
    document.body.addEventListener('touchend', this.startEnd, false);
    document.body.addEventListener('touchcancel', this.startEnd, false);

    this.$once('hook:beforeDestroy', () => {
      document.body.removeEventListener('mousemove', this.startMove);
      document.body.removeEventListener('mouseup', this.startEnd);
      document.body.removeEventListener('mouseleave', this.startEnd);
      document.body.removeEventListener('touchmove', this.startMove);
      document.body.removeEventListener('touchend', this.startEnd);
      document.body.removeEventListener('touchcancel', this.startEnd);
    });
  },
  watch: {
    scale() {
      this.updateIframe();
    },
    shape() {
      if (this.shape === 'circle') {
        const size = Math.max(this.rect.width, this.rect.height);
        this.rect.width = this.rect.height = Math.min(size, this.maxSize.width);
      }
    },
  },
  methods: {
    bindEvents() {
      const quit = (e) => {
        if (e.keyCode === 27) {
          this.closeMagnifier();
        }
      };

      let timeId;
      const updateScreenshot = () => {
        if (timeId) clearTimeout(timeId);

        timeId = setTimeout(() => {
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          this.scale = 1.5;
          this.windowWidth = windowWidth;
          this.windowHeight = windowHeight;
          this.updateRect(this.calcInitRect(windowWidth, windowHeight));

          $('.html2canvas-container').attr({
            width: windowWidth,
            height: windowHeight,
          });
        }, 50);
      };

      window.addEventListener('resize', updateScreenshot, false);
      window.addEventListener('keydown', quit, false);

      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', updateScreenshot);
        window.removeEventListener('keydown', quit);
      });
    },
    finish() {
      this.updateIframe(true);
      this.finishRendering = true;
    },
    closeMagnifier() {
      this.$emit('closeMagnifier');
    },
    updateIframe(updateRef) {
      if (updateRef) {
        this.refPoint.top = this.rect.top + this.rect.height / 2;
        this.refPoint.left = this.rect.left + this.rect.width / 2;
      }

      const top = (this.refPoint.top * (this.scale - 1) + this.rect.top) / this.scale;
      const left = (this.refPoint.left * (this.scale - 1) + this.rect.left) / this.scale;

      const transform = `scale(${this.scale}) translate(${-left}px, ${-top}px)`;
      const $container = $('.html2canvas-container').get(0);

      $container.style.transform = transform;
      $container.style['-webkit-transform'] = transform;
    },
    startDown(e) {
      e.preventDefault();
      this.isMove = true;

      let pageX;
      let pageY;

      if (e.touches) {
        pageX = e.touches[0].clientX;
        pageY = e.touches[0].clientY;
      } else {
        pageX = e.pageX;
        pageY = e.pageY;
      }

      this.disX = pageX - e.target.offsetLeft;
      this.disY = pageY - e.target.offsetTop;
      this.lastRect.left = this.rect.left;
      this.lastRect.top = this.rect.top;
    },
    startMove(e) {
      if (this.isMove) {
        this.startMoving = true;

        let pageX;
        let pageY;

        if (e.touches) {
          pageX = e.touches[0].clientX;
          pageY = e.touches[0].clientY;
        } else {
          pageX = e.pageX;
          pageY = e.pageY;
        }

        this.rect.left = Math.max(this.lastRect.left + pageX - this.disX, -(this.rect.width / 2));
        this.rect.left = (this.rect.left + this.rect.width > window.innerWidth + this.rect.width / 2) ? 
          window.innerWidth - this.rect.width / 2 : 
          this.rect.left;
          
        this.rect.top = Math.max(this.lastRect.top + pageY - this.disY, -(this.rect.height / 2));
        this.rect.top = (this.rect.top + this.rect.height > window.innerHeight + this.rect.height / 2) ? 
          window.innerHeight - this.rect.height / 2 : 
          this.rect.top;

        this.updateIframe(true);
      }
    },
    startEnd(e) {
      if (this.isMove) {
        e.preventDefault();
        this.isMove = false;
        this.startMoving = false;
      }
    },
    formatRect(rect, i) {      
      if (this.shape === 'circle') {
        let diff = 0;
        let onHorizontalEdge = false;
        let onVerticalEdge = false;

        if (this.rect.left + this.rect.width / 2 <= 0) {
          const moveOut = Math.max(this.lastRect.left + this.lastRect.width / 2, 0);
          const diff = this.lastRect.width - moveOut - rect.width - (this.lastRect.width - this.rect.width) / 2;
          
          rect.left = this.rect.left + diff;
          rect.top = this.rect.top + diff;
          rect.height = rect.width = this.rect.width - diff * 2;
          onHorizontalEdge = true;
        } else if (this.rect.left + this.rect.width / 2 >= window.innerWidth) {
          const moveOut = Math.max(window.innerWidth - this.lastRect.left - this.lastRect.width / 2, 0);
          const diff = this.lastRect.width - moveOut - rect.width - (this.lastRect.width - this.rect.width) / 2;
          
          rect.left = this.rect.left + diff;
          rect.top = this.rect.top + diff;
          rect.height = rect.width = this.rect.width - diff * 2;
          onHorizontalEdge = true;
        }

        if (this.rect.top + this.rect.height / 2 <= 0) {
          const moveOut = Math.max(this.lastRect.top + this.lastRect.height / 2, 0);
          const diff = this.lastRect.height - moveOut - rect.height - (this.lastRect.height - this.rect.height) / 2;
          
          rect.left = this.rect.left + diff;
          rect.top = this.rect.top + diff;
          rect.height = rect.width = this.rect.width - diff * 2;
          onVerticalEdge = true;
        } else if (this.rect.top + this.rect.height / 2 >= window.innerHeight) {
          const moveOut = Math.max(window.innerHeight - this.lastRect.top - this.lastRect.height / 2, 0);
          const diff = this.lastRect.height - moveOut - rect.height - (this.lastRect.height - this.rect.height) / 2;
          
          rect.left = this.rect.left + diff;
          rect.top = this.rect.top + diff;
          rect.height = rect.width = this.rect.width - diff * 2;
          onVerticalEdge = true;
        }

        if (rect.width < this.minSize) {
          diff = this.minSize - rect.width;
          rect.height = rect.width = this.minSize;
        }

        if (rect.width > this.maxSize.width) {
          diff = this.maxSize.width - rect.width;
          rect.height = rect.width = this.maxSize.width;
        }

        if (diff !== 0) {
          switch (i) {
            case 1:
              if (onHorizontalEdge || onVerticalEdge) {
                rect.left -= diff / 2;
                rect.top -= diff / 2;
              } else {
                rect.left -= diff;
                rect.top -= diff;
              }
              break;

            case 2:
              if (onVerticalEdge) {
                rect.top -= diff / 2;
                rect.left -= diff / 2;
              } else {
                rect.top -= diff;
                rect.left -= diff / 2;
              }
              break;

            case 3:
              if (onHorizontalEdge || onVerticalEdge) {
                rect.left -= diff / 2;
                rect.top -= diff / 2;
              } else {
                rect.top -= diff;
              }
              break;

            case 4:
              if (onHorizontalEdge) {
                rect.left -= diff / 2;
              }
              rect.top -= diff / 2;
              break;

            case 5:
              if (onHorizontalEdge || onVerticalEdge) {
                rect.left -= diff / 2;
                rect.top -= diff / 2;
              }
              break;

            case 6:
              if (onVerticalEdge) {
                rect.top -= diff / 2;
              }
              rect.left -= diff / 2;
              break;

            case 7:
              if (onHorizontalEdge || onVerticalEdge) {
                rect.left -= diff / 2;
                rect.top -= diff / 2;
              } else {
                rect.left -= diff;
              }
              break;

            case 8:
              if (onHorizontalEdge) {
                rect.left -= diff / 2;
                rect.top -= diff / 2;
              } else {
                rect.left -= diff;
                rect.top -= diff / 2;
              }
              break;
          }
        }
      } else {
        let onHorizontalEdge = false;
        let onVerticalEdge = false;

        if (this.rect.left + this.rect.width / 2 <= 0) {
          const moveOut = Math.max(this.lastRect.left + this.lastRect.width / 2, 0);
          const diff = this.lastRect.width - moveOut - rect.width - (this.lastRect.width - this.rect.width) / 2;
          
          rect.left = this.rect.left + diff;
          rect.width = this.rect.width - diff * 2;
          onHorizontalEdge = true;
        } else if (this.rect.left + this.rect.width / 2 >= window.innerWidth) {
          rect.width = (window.innerWidth - rect.left) * 2;
          onHorizontalEdge = true;
        }

        if (this.rect.top + this.rect.height / 2 <= 0) {
          const moveOut = Math.max(this.lastRect.top + this.lastRect.height / 2, 0);
          const diff = this.lastRect.height - moveOut - rect.height - (this.lastRect.height - this.rect.height) / 2;
          
          rect.top = this.rect.top + diff;
          rect.height = this.rect.height - diff * 2;
          onVerticalEdge = true;
        } else if (this.rect.top + this.rect.height / 2 >= window.innerHeight) {
          rect.height = (window.innerHeight - rect.top) * 2;
          onVerticalEdge = true;
        }

        if (rect.width < this.minSize) {
          const diff = this.minSize - rect.width;
          rect.width = this.minSize;

          if (onHorizontalEdge) {
            rect.left -= diff / 2;
          } else if (i === 1 || i === 7 || i === 8) {
            rect.left -= diff;
          }
        }

        if (rect.height < this.minSize) {
          const diff = this.minSize - rect.height;
          rect.height = this.minSize;

          if (onVerticalEdge) {
            rect.top -= diff / 2;
          } else if (i <= 3) {
            rect.top -= diff;
          }
        }

        if (rect.width > this.maxSize.width) {
          const diff = this.maxSize.width - rect.width;
          rect.width = this.maxSize.width;

          if (onHorizontalEdge) {
            rect.left -= diff / 2;
          } else if (i === 1 || i === 7 || i === 8) {
            rect.left -= diff;
          }
        }

        if (rect.height > this.maxSize.height) {
          const diff = this.maxSize.height - rect.height;
          rect.height = this.maxSize.height;

          if (onVerticalEdge) {
            rect.top -= diff / 2;
          } else if (i <= 3) {
            rect.top -= diff;
          }
        }
      }

      return rect;
    },
    updateRect(rect) {
      this.rect.left = rect.left;
      this.rect.top = rect.top;
      this.rect.width = rect.width;
      this.rect.height = rect.height;

      this.updateIframe(true);
    },
    onRectResizeStart() {
      this.lastRect.top = this.rect.top;
      this.lastRect.left = this.rect.left;
      this.lastRect.width = this.rect.width;
      this.lastRect.height = this.rect.height;
    },
    onRectResize({ x, y, i, }) {
      this.isResizing = true;

      let rect = {};
      let width;
      let height;

      switch (i) {
        case 1:
          if (this.shape === 'circle') {
            if ((x > 0 && y < 0) || (x < 0 && y > 0)) {
              y = -y;
            }

            y = Math.abs(x) * (y >= 0 ? 1 : -1);
          }
          
          rect = {
            width: this.lastRect.width - x,
            height: this.lastRect.height - y,
            left: this.lastRect.left + x,
            top: this.lastRect.top + y,
          };
          break;

        case 2:
          if (this.shape === 'circle') {
            rect = {
              width: this.lastRect.width - y,
              height: this.lastRect.height - y,
              top: this.lastRect.top + y,
              left: this.lastRect.left + y / 2,
            };
          } else {
            rect = {
              height: this.lastRect.height - y,
              top: this.lastRect.top + y,
            };
          }
          break;

        case 3:
          if (this.shape === 'circle') {
            if ((x > 0 && y > 0) || (x < 0 && y < 0)) {
              y = -y;
            }

            y = Math.abs(x) * (y >= 0 ? 1 : -1);
          }

          rect = {
            width: this.lastRect.width + x,
            height: this.lastRect.height - y,
            top: this.lastRect.top + y,
          };
          break;

        case 4:
          if (this.shape === 'circle') {
            rect = {
              width: this.lastRect.width + x,
              height: this.lastRect.height + x,
              top: this.lastRect.top - x / 2,
            };
          } else {
            rect = { 
              width: this.lastRect.width + x,
            };
          }
          break;

        case 5:
          if (this.shape === 'circle') {
            width = this.lastRect.width + Math.max(x, y);
            height = this.lastRect.height + Math.max(x, y);
          } else {
            width = this.lastRect.width + x;
            height = this.lastRect.height + y;
          }

          rect = {
            width,
            height,
          };
          break;

        case 6:
          if (this.shape === 'circle') {
            rect = {
              width: this.lastRect.width + y,
              height: this.lastRect.height + y,
              left: this.lastRect.left - y / 2
            };
          } else {
            rect = {
              height: this.lastRect.height + y,
            };
          }
          break;
          
        case 7:
          if (this.shape === 'circle') {
            if ((x > 0 && y > 0) || (x < 0 && y < 0)) {
              y = -y;
            }

            y = Math.abs(x) * (y >= 0 ? 1 : -1);
          }

          rect = {
            left: this.lastRect.left + x,
            width: this.lastRect.width - x,
            height: this.lastRect.height + y,
          };
          break;

        case 8:
          if (this.shape === 'circle') {
            rect = {
              width: this.lastRect.width - x,
              height: this.lastRect.height - x,
              left: this.lastRect.left + x,
              top: this.lastRect.top + x / 2,
            };
          } else {
            rect = {
              width: this.lastRect.width - x,
              left: this.lastRect.left + x,
            };
          }
          break;
      }

      rect = this.formatRect(Object.assign({}, this.rect, rect), i);
      this.updateRect(rect);
    },
    onRectResizeEnd() {
      this.isResizing = false;
    },
    calcMaxSize(windowWidth, windowHeight) {
      const gap = 32;
      const toolbarWidth = 72;
      let maxContentWidth = windowWidth - (toolbarWidth + gap) * 2;
      let maxContentHeight = windowHeight - (toolbarWidth + gap) * 2;

      if (this.shape !== 'rect') {
        maxContentWidth = maxContentHeight = Math.min(maxContentWidth, maxContentHeight);
      }

      return {
        width: maxContentWidth,
        height: maxContentHeight,
      };
    },
    calcInitRect(windowWidth, windowHeight) {
      const percent = this.type === 'magnifier' ? 0.5 : 0.7;
      const minWidth = 407; // 右侧工具条 size
      const maxWidth = this.type === 'magnifier' ? 540 : 864;

      let width = Math.min(Math.floor(Math.min(windowWidth, windowHeight) * percent), maxWidth);
      width = Math.max(minWidth, width);
      width = Math.min(width, this.calcMaxSize(windowWidth, windowHeight).width);

      const height = width;

      const left = (windowWidth - width) / 2;
      const top = (windowHeight - height) / 2;

      return {
        width,
        height,
        top,
        left,
      };
    },
  }
}
</script>

<style lang="scss" scoped>
.magnifier {
  position: absolute;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .3);

  &.dark {
    background-color: rgba(0, 0, 0, .75);
  }
}

.content-wrap {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
}

.content-inner {
  display: flex;
  align-items: flex-end;
}

.hide {
  visibility: hidden;
}

.content-entry {
  position: relative;
  z-index: 2;
  overflow: hidden;
  cursor: move;
  transform: translateX(0);

  &::after {
    content: '';
    width: calc(100% - 6px);
    height: calc(100% - 6px);
    position: absolute;
    top: 0;
    left: 0;
  }

  &.circle {
    border-radius: 50%;

    &::after {
      border-radius: 50%;
      border: 3px solid #FF542C;
    }
  }

  .content__entry {
    transform: scale(1);
  }

  ::v-deep {
    .html2canvas-container {
      user-select: none;
      pointer-events: none;
      background-color: #fff;
      transform-origin: 0px 0px;
    }

    .electron-screenshot {
      width: 100vw;
    }
  }
}
</style>